import { useEffect, useRef, useState, useCallback, useMemo, type FC } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type {
  GeoJSONFeatureCollection,
  PlzAgeFeatureCollection,
} from "../../services/heatmap";
import {
  getCustomerLocationsWithFilter,
  fetchFilterMetadata,
  getPlzAgeChoropleth,
} from "../../services/heatmap";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { Breadcrumb, BreadcrumbItem, Badge, Spinner } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { useThemeMode } from "../../hooks/useThemeMode";
import { useAuth } from "../../contexts/AuthContext";
import type {
  FilterGroup,
  HeatmapSavedFilter,
  SystemPreset,
} from "../../types/filter";
import {
  getSystemPresets,
  getSavedPresets,
  savePreset,
  deletePreset,
  setDefaultPreset,
  saveActiveFilterState,
  getActiveFilterState,
} from "../../services/heatmap-presets";
import { FilterBar } from "../../components/filter-builder/FilterBar";
import type { HeatmapMetric } from "../../config/heatmap-metrics";
import { METRIC_CONFIGS } from "../../config/heatmap-metrics";
import { MetricSelector } from "../../components/heatmap/MetricSelector";
import { HeatmapLegend } from "../../components/heatmap/HeatmapLegend";

const BERLIN_COORDINATES = { latitude: 52.52, longitude: 13.405 };

const MAPBOX_STYLES = {
  light: "mapbox://styles/mapbox/light-v11",
  dark: "mapbox://styles/mapbox/dark-v11",
} as const;

type ThemeMode = keyof typeof MAPBOX_STYLES;

const CustomerHeatmapPage: FC = () => {
  const { theme } = useThemeMode();
  const { user } = useAuth();
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isUpdatingStyle, setIsUpdatingStyle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pointCount, setPointCount] = useState<number | null>(null);

  // Keep latest GeoJSON in ref so style reload or load event can consume it
  const latestGeoData = useRef<GeoJSONFeatureCollection | null>(null);

  // PLZ choropleth payload for the age metric. Kept in a ref for the same
  // reason: setStyle() wipes all custom sources, so both layers are rebuilt
  // from these refs inside the "style.load" handler.
  const latestPlzData = useRef<PlzAgeFeatureCollection | null>(null);

  // Tracks the style URL currently applied to the map, so we never call
  // setStyle() with the style it already has (Mapbox no-ops that call and
  // never fires "style.load", which would leave the overlay stuck forever)
  const appliedStyle = useRef<string | null>(null);

  // Condition Builder Filter & Presets state
  const [activeFilter, setActiveFilter] = useState<FilterGroup | null>(null);
  // Mirror of activeFilter for stable callbacks (same pattern as activeMetricRef)
  const activeFilterRef = useRef<FilterGroup | null>(null);
  const [systemPresets] = useState<SystemPreset[]>(() => getSystemPresets());
  const [userPresets, setUserPresets] = useState<HeatmapSavedFilter[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  // Multi-Metric Heatmap state
  const [activeMetric, setActiveMetric] = useState<HeatmapMetric>("contracts");
  const activeMetricRef = useRef<HeatmapMetric>("contracts");
  activeMetricRef.current = activeMetric;
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  // Dynamically update paint properties without rebuilding layers or refetching.
  //
  // This iterates the config objects rather than listing property names: a
  // hardcoded list silently drops any newly added paint key on metric switch,
  // which is exactly how circle-stroke-opacity would have gone stale and
  // reintroduced the white-out bug.
  const applyMetricPaint = useCallback((metric: HeatmapMetric) => {
    if (!map.current) return;
    const config = METRIC_CONFIGS[metric];
    // Object.entries widens paint keys to `string`, but setPaintProperty is
    // typed with a literal union of every valid key. Values come from a typed
    // config, so a loose local alias is safe and keeps the loop generic.
    const setPaint = (layerId: string, paint: Record<string, unknown>) => {
      if (!map.current?.getLayer(layerId)) return;
      for (const [prop, value] of Object.entries(paint)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (map.current.setPaintProperty as any)(layerId, prop, value);
      }
    };

    setPaint("customer-heat", config.heatmapPaint);
    setPaint("customer-point", config.circlePaint);
    if (config.fillPaint) {
      setPaint("plz-age-fill", config.fillPaint);
    }
  }, []);

  // Show/hide whole layers per metric. Previously the age metric faked this by
  // zeroing every heatmap paint value; real visibility toggling lets each
  // metric own only the layers it actually uses.
  const applyMetricVisibility = useCallback((metric: HeatmapMetric) => {
    if (!map.current) return;
    const config = METRIC_CONFIGS[metric];

    const setVisible = (layerId: string, visible: boolean) => {
      if (!map.current?.getLayer(layerId)) return;
      map.current.setLayoutProperty(
        layerId,
        "visibility",
        visible ? "visible" : "none"
      );
    };

    setVisible("customer-heat", config.heatmapVisible);
    setVisible("customer-point", config.id !== "age");
    setVisible("plz-age-fill", config.plzFillVisible);
    setVisible("plz-age-outline", config.plzFillVisible);
  }, []);



  // Update or attach Mapbox layers using the provided GeoJSON
  const renderHeatmapLayers = useCallback((geoData: GeoJSONFeatureCollection) => {
    if (!map.current) return;

    try {
      const existingSource = map.current.getSource("customers") as mapboxgl.GeoJSONSource | undefined;

      if (existingSource) {
        // Fast in-place data update: does not tear down layers or flicker
        existingSource.setData(geoData as GeoJSON.FeatureCollection);
        return;
      }

      // If source doesn't exist yet, register source and layers
      map.current.addSource("customers", {
        type: "geojson",
        data: geoData as GeoJSON.FeatureCollection,
      });

      const beforeLayerId = map.current.getLayer("waterway-label") ? "waterway-label" : undefined;
      const currentConfig = METRIC_CONFIGS[activeMetricRef.current];

      // Bake visibility in at creation: if the age metric is already active
      // when this data first arrives, the point layers must not flash into
      // view before applyMetricVisibility runs.
      const pointsVisible = currentConfig.id !== "age";

      // Add heatmap layer
      map.current.addLayer(
        {
          id: "customer-heat",
          type: "heatmap",
          source: "customers",
          maxzoom: 15,
          layout: {
            visibility:
              pointsVisible && currentConfig.heatmapVisible ? "visible" : "none",
          },
          paint: currentConfig.heatmapPaint,
        },
        beforeLayerId
      );

      // Add circle layer for individual points
      map.current.addLayer(
        {
          id: "customer-point",
          type: "circle",
          source: "customers",
          layout: { visibility: pointsVisible ? "visible" : "none" },
          paint: currentConfig.circlePaint,
        },
        beforeLayerId
      );

      // Add hover tooltip interaction on customer points
      map.current.on("mouseenter", "customer-point", (e) => {
        if (!map.current || !e.features || !e.features[0]) return;
        map.current.getCanvas().style.cursor = "pointer";

        const feature = e.features[0];
        const coordinates = (feature.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
        const props = feature.properties as {
          customer_count?: number;
          total_commission?: number;
          avg_customer_age?: number | null;
        };

        const count = props?.customer_count || 1;
        const commission = props?.total_commission
          ? `${Number(props.total_commission).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
          : "0,00 €";
        const age = props?.avg_customer_age != null ? `${props.avg_customer_age} Jahre` : "Keine Angabe";

        const html = `
          <div style="font-family: inherit; padding: 4px 6px; font-size: 12px; line-height: 1.4;">
            <div style="font-weight: 700; margin-bottom: 4px; border-bottom: 1px solid #e5e7eb; padding-bottom: 2px;">Standort-Details</div>
            <div><strong>Verträge:</strong> ${count}</div>
            <div><strong>Provision:</strong> ${commission}</div>
            <div><strong>Ø Alter:</strong> ${age}</div>
          </div>
        `;

        if (!popupRef.current) {
          popupRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 10,
          });
        }

        popupRef.current.setLngLat(coordinates).setHTML(html).addTo(map.current);
      });

      map.current.on("mouseleave", "customer-point", () => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = "";
        popupRef.current?.remove();
      });
    } catch (err) {
      console.error("Error configuring map layers:", err);
      setError("Fehler beim Konfigurieren der Kartenlayer");
    }
  }, []);

  // Render the PLZ choropleth for the age metric.
  //
  // Deliberately separate from renderHeatmapLayers: that function early-returns
  // when the "customers" source exists, which would prevent a second source
  // from ever being created on subsequent calls.
  const renderPlzLayer = useCallback((plzData: PlzAgeFeatureCollection) => {
    if (!map.current) return;

    try {
      const existingSource = map.current.getSource("plz-age") as
        | mapboxgl.GeoJSONSource
        | undefined;

      if (existingSource) {
        existingSource.setData(plzData as unknown as GeoJSON.FeatureCollection);
        return;
      }

      map.current.addSource("plz-age", {
        type: "geojson",
        data: plzData as unknown as GeoJSON.FeatureCollection,
      });

      const config = METRIC_CONFIGS.age;
      const visible = activeMetricRef.current === "age";

      // Insert beneath the point layers so polygons never occlude them.
      const beforeLayerId = map.current.getLayer("customer-heat")
        ? "customer-heat"
        : map.current.getLayer("waterway-label")
          ? "waterway-label"
          : undefined;

      map.current.addLayer(
        {
          id: "plz-age-fill",
          type: "fill",
          source: "plz-age",
          layout: { visibility: visible ? "visible" : "none" },
          paint: config.fillPaint!,
        },
        beforeLayerId
      );

      map.current.addLayer(
        {
          id: "plz-age-outline",
          type: "line",
          source: "plz-age",
          layout: { visibility: visible ? "visible" : "none" },
          paint: {
            "line-color": "rgba(15,23,42,0.35)",
            "line-width": 0.5,
          },
        },
        beforeLayerId
      );

      // Polygon hover: uses e.lngLat, since a polygon has no single coordinate
      // the way the point layer's geometry does.
      map.current.on("mousemove", "plz-age-fill", (e) => {
        if (!map.current || !e.features || !e.features[0]) return;
        map.current.getCanvas().style.cursor = "pointer";

        const props = e.features[0].properties as {
          zip_code?: string;
          median_age?: number;
          customer_count?: number;
          is_reliable?: boolean | string;
        };

        const reliable = props.is_reliable === true || props.is_reliable === "true";
        const count = props.customer_count ?? 0;

        const html = `
          <div style="font-family: inherit; padding: 4px 6px; font-size: 12px; line-height: 1.4;">
            <div style="font-weight: 700; margin-bottom: 4px; border-bottom: 1px solid #e5e7eb; padding-bottom: 2px;">PLZ ${props.zip_code ?? "-"}</div>
            ${
              reliable
                ? `<div><strong>Medianalter:</strong> ${props.median_age ?? "-"} Jahre</div>`
                : `<div style="color:#b45309;"><strong>Zu wenig Daten</strong></div>`
            }
            <div><strong>Kunden:</strong> ${count}</div>
          </div>
        `;

        if (!popupRef.current) {
          popupRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 10,
          });
        }

        popupRef.current.setLngLat(e.lngLat).setHTML(html).addTo(map.current);
      });

      map.current.on("mouseleave", "plz-age-fill", () => {
        if (!map.current) return;
        map.current.getCanvas().style.cursor = "";
        popupRef.current?.remove();
      });
    } catch (err) {
      console.error("Error configuring PLZ layer:", err);
      setError("Fehler beim Konfigurieren der PLZ-Karte");
    }
  }, []);

  // Fetch the PLZ age aggregates (and boundary polygons) on demand. Unlike the
  // point metrics, which all read from one payload, the age metric needs its
  // own query - so switching to it triggers a load.
  const loadPlzAgeData = useCallback(
    async (filterConfig: FilterGroup | null) => {
      setIsFetchingData(true);
      try {
        const plzData = await getPlzAgeChoropleth(filterConfig);
        latestPlzData.current = plzData;
        if (map.current && isMapReady) {
          renderPlzLayer(plzData);
        }
      } catch (err) {
        console.error("Fehler beim Laden der PLZ-Altersdaten:", err);
        setError("Fehler beim Laden der Altersdaten");
      } finally {
        setIsFetchingData(false);
      }
    },
    [isMapReady, renderPlzLayer]
  );

  const handleMetricChange = useCallback(
    (newMetric: HeatmapMetric) => {
      setActiveMetric(newMetric);
      activeMetricRef.current = newMetric;
      applyMetricPaint(newMetric);
      applyMetricVisibility(newMetric);

      // The age metric is backed by its own RPC + boundary file, so it cannot
      // be served from the shared point payload like the other two.
      if (newMetric === "age" && !latestPlzData.current) {
        void loadPlzAgeData(activeFilterRef.current);
      }
    },
    [applyMetricPaint, applyMetricVisibility, loadPlzAgeData]
  );

  // Initialize Mapbox map instance ONCE on mount
  useEffect(() => {
    const accessToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"];
    if (!accessToken) {
      setError("Mapbox-Zugriffstoken fehlt");
      return;
    }

    mapboxgl.accessToken = accessToken;
    if (!mapContainer.current || map.current) return;

    const initialStyle = MAPBOX_STYLES[theme as ThemeMode] || MAPBOX_STYLES.light;
    appliedStyle.current = initialStyle;
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle,
      center: [BERLIN_COORDINATES.longitude, BERLIN_COORDINATES.latitude],
      zoom: 10,
    });

    mapInstance.addControl(new mapboxgl.NavigationControl(), "top-right");

    mapInstance.once("load", () => {
      map.current = mapInstance;
      setIsMapReady(true);
      if (latestGeoData.current) {
        renderHeatmapLayers(latestGeoData.current);
      }
      if (latestPlzData.current) {
        renderPlzLayer(latestPlzData.current);
      }
      applyMetricVisibility(activeMetricRef.current);
    });

    return () => {
      setIsMapReady(false);
      popupRef.current?.remove();
      popupRef.current = null;
      mapInstance.remove();
      map.current = null;
    };
  }, []); // Only run once on mount

  // Handle Theme changes smoothly without destroying the map
  useEffect(() => {
    if (!map.current || !isMapReady) return;

    const newStyle = MAPBOX_STYLES[theme as ThemeMode] || MAPBOX_STYLES.light;

    // The map already has this style (e.g. this effect re-running on mount when
    // isMapReady flips to true). setStyle() would no-op and "style.load" would
    // never fire, leaving the overlay visible forever.
    if (appliedStyle.current === newStyle) return;

    appliedStyle.current = newStyle;
    setIsUpdatingStyle(true);
    map.current.setStyle(newStyle);

    map.current.once("style.load", () => {
      // setStyle() wipes every custom source and layer, so both the point
      // layers and the PLZ choropleth must be rebuilt from the refs.
      if (latestGeoData.current) {
        renderHeatmapLayers(latestGeoData.current);
      }
      if (latestPlzData.current) {
        renderPlzLayer(latestPlzData.current);
      }
      applyMetricVisibility(activeMetricRef.current);
      setIsUpdatingStyle(false);
    });
  }, [theme, isMapReady, renderHeatmapLayers, renderPlzLayer, applyMetricVisibility]);

  // Execute explicit filter query against server RPC
  const executeFilterQuery = useCallback(
    async (filterConfig: FilterGroup | null) => {
      setIsFetchingData(true);
      setError(null);
      activeFilterRef.current = filterConfig;

      try {
        const geoData = await getCustomerLocationsWithFilter(filterConfig);

        latestGeoData.current = geoData;
        setPointCount(geoData.features.length);

        if (map.current && isMapReady) {
          renderHeatmapLayers(geoData);
        }

        // The PLZ choropleth honours the same filter tree, so it must be
        // refetched whenever the filter changes while the age metric is shown.
        if (activeMetricRef.current === "age") {
          const plzData = await getPlzAgeChoropleth(filterConfig);
          latestPlzData.current = plzData;
          if (map.current && isMapReady) {
            renderPlzLayer(plzData);
          }
        } else {
          // Invalidate so switching to age refetches with the new filter.
          latestPlzData.current = null;
        }

        // Save active state to localStorage for automatic reload recovery
        if (user?.id) {
          saveActiveFilterState(user.id, filterConfig);
        }
      } catch (err) {
        console.error("Fehler beim Laden der gefilterten Kundendaten:", err);
        setError("Fehler beim Laden der Kundendaten");
      } finally {
        setIsFetchingData(false);
      }
    },
    [isMapReady, renderHeatmapLayers, renderPlzLayer, user?.id]
  );

  // Load saved presets and restore active or default filter view on initial mount
  useEffect(() => {
    let isMounted = true;

    const initializeFilters = async () => {
      // Warm up filter metadata in background
      fetchFilterMetadata().catch((err) =>
        console.warn("Metadata pre-fetch failed:", err)
      );

      if (!user?.id) {
        // Unauthenticated initial fetch
        executeFilterQuery(null);
        return;
      }

      try {
        const presets = await getSavedPresets(user.id);
        if (!isMounted) return;
        setUserPresets(presets);

        // 1. Check for last active draft state in localStorage
        const activeDraft = getActiveFilterState(user.id);
        if (activeDraft) {
          setActiveFilter(activeDraft);
          executeFilterQuery(activeDraft);
          return;
        }

        // 2. Check for user default preset
        const defaultPreset = presets.find((p) => p.is_default);
        if (defaultPreset) {
          setActivePresetId(defaultPreset.id);
          setActiveFilter(defaultPreset.filter_config);
          executeFilterQuery(defaultPreset.filter_config);
          return;
        }

        // 3. Fallback: Load unfiltered heatmap
        executeFilterQuery(null);
      } catch (err) {
        console.error("Error initializing presets:", err);
        if (isMounted) executeFilterQuery(null);
      }
    };

    initializeFilters();

    return () => {
      isMounted = false;
    };
  }, [user?.id, executeFilterQuery]);

  // Preset Action Handlers
  const handleSelectPreset = (preset: SystemPreset | HeatmapSavedFilter) => {
    setActivePresetId(preset.id);
    setActiveFilter(preset.filter_config);
    executeFilterQuery(preset.filter_config);
  };

  const handleSavePreset = async (
    name: string,
    description: string,
    isDefault: boolean
  ) => {
    if (!user?.id) return;
    const filterToSave = activeFilter || {
      id: "root",
      type: "group",
      conjunction: "and",
      children: [],
    };

    await savePreset(user.id, name, description, filterToSave, isDefault);
    const refreshed = await getSavedPresets(user.id);
    setUserPresets(refreshed);
  };

  const handleDeletePreset = async (presetId: string) => {
    if (!user?.id) return;
    await deletePreset(user.id, presetId);
    const refreshed = await getSavedPresets(user.id);
    setUserPresets(refreshed);
    if (activePresetId === presetId) {
      setActivePresetId(null);
    }
  };

  const handleSetDefaultPreset = async (presetId: string) => {
    if (!user?.id) return;
    await setDefaultPreset(user.id, presetId);
    const refreshed = await getSavedPresets(user.id);
    setUserPresets(refreshed);
  };

  const handleFilterChange = (newFilter: FilterGroup | null) => {
    setActivePresetId(null);
    setActiveFilter(newFilter);
    executeFilterQuery(newFilter);
  };

  // Compute aggregate statistics based on loaded GeoJSON
  const aggregateStats = useMemo(() => {
    if (!latestGeoData.current || !latestGeoData.current.features.length) {
      return {
        locations: pointCount ?? 0,
        totalCustomers: 0,
        totalCommission: 0,
        avgAge: null as number | null,
      };
    }

    let totalCustomers = 0;
    let totalCommission = 0;
    let ageSum = 0;
    let ageCount = 0;

    for (const feature of latestGeoData.current.features) {
      const props = feature.properties;
      const count = props.customer_count || 1;
      totalCustomers += count;
      totalCommission += props.total_commission || 0;
      // Apply the same caps the PLZ RPC uses, so the headline figure agrees
      // with the map: ages <= 18 are sales-rep placeholder birthdates and
      // anything over 100 is impossible (the raw data reaches 1077).
      const age = props.avg_customer_age;
      if (age != null && !isNaN(age) && age > 18 && age <= 100) {
        ageSum += age * count;
        ageCount += count;
      }
    }

    return {
      locations: latestGeoData.current.features.length,
      totalCustomers,
      totalCommission,
      avgAge: ageCount > 0 ? Math.round((ageSum / ageCount) * 10) / 10 : null,
    };
  }, [pointCount]);

  return (
    <NavbarSidebarLayout pageTitle="Kunden Heatmap">
      <div className="p-4">
        <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex lg:mt-1.5">
          <div className="mb-1 w-full">
            <div className="mb-4">
              <Breadcrumb className="mb-4">
                <BreadcrumbItem href="#">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">Startseite</span>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbItem>Kunden Heatmap</BreadcrumbItem>
              </Breadcrumb>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  Kunden Heatmap
                </h1>
                {pointCount !== null && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge color="info" size="sm" className="text-sm font-medium">
                      {aggregateStats.locations.toLocaleString("de-DE")} Standorte
                    </Badge>
                    {activeMetric === "contracts" && (
                      <Badge color="gray" size="sm" className="text-sm font-medium">
                        {aggregateStats.totalCustomers.toLocaleString("de-DE")} Verträge
                      </Badge>
                    )}
                    {activeMetric === "commission" && (
                      <Badge color="success" size="sm" className="text-sm font-medium">
                        {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(aggregateStats.totalCommission)} Gesamtprovision
                      </Badge>
                    )}
                    {activeMetric === "age" && (
                      <Badge color="purple" size="sm" className="text-sm font-medium">
                        {aggregateStats.avgAge != null ? `Ø ${aggregateStats.avgAge} Jahre` : "Keine Altersdaten"}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {/* Advanced Condition Builder Filter Bar */}
          <FilterBar
            activeFilter={activeFilter}
            onFilterChange={handleFilterChange}
            systemPresets={systemPresets}
            userPresets={userPresets}
            activePresetId={activePresetId}
            onSelectPreset={handleSelectPreset}
            onSavePreset={handleSavePreset}
            onDeletePreset={handleDeletePreset}
            onSetDefaultPreset={handleSetDefaultPreset}
            pointCount={pointCount}
            isLoading={isFetchingData}
          />

          <div className="relative h-[650px] rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
            {/* Loading indicator bar */}
            {isFetchingData && (
              <div className="absolute top-6 left-6 z-20 flex items-center gap-2 rounded-lg bg-white/90 px-3 py-1.5 shadow dark:bg-gray-800/90">
                <Spinner size="sm" aria-label="Daten werden geladen" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  Heatmap-Daten werden geladen...
                </span>
              </div>
            )}

            {/* Metric Selector toggle pills */}
            <div className="absolute top-6 right-16 z-20">
              <MetricSelector
                activeMetric={activeMetric}
                onMetricChange={handleMetricChange}
              />
            </div>

            {/* Heatmap Legend */}
            <div className="absolute bottom-6 left-6 z-20 max-w-xs sm:max-w-sm">
              <HeatmapLegend activeMetric={activeMetric} />
            </div>

            {isUpdatingStyle && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-100 bg-opacity-75 dark:bg-gray-700 dark:bg-opacity-75">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  Kartenstil wird aktualisiert...
                </div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-red-100 bg-opacity-75 dark:bg-red-900 dark:bg-opacity-75">
                <div className="text-lg font-semibold text-red-900 dark:text-red-100">
                  {error}
                </div>
              </div>
            )}
            <div ref={mapContainer} className="h-full w-full rounded-lg" />
          </div>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CustomerHeatmapPage;
